import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PhotoIcon, SparklesIcon, ArrowUpTrayIcon, PencilIcon, TrashIcon, CheckBadgeIcon } from '@heroicons/react/24/outline';
import { IProjectAsset, AssetType, CharacterRoleLabels, CharacterRoleColors, AssetGenerationStatus, getLabelsByLocale, Locale } from '@packages/global';
import LazyImage from '../common/LazyImage';
import MediaPreviewModal from '../common/MediaPreviewModal';

interface AssetGridProps {
  assets: IProjectAsset[];
  loading?: boolean;
  onEdit: (asset: IProjectAsset) => void;
  onDelete: (asset: IProjectAsset) => void;
  onGenerate?: (asset: IProjectAsset) => void;
  onUpload?: (asset: IProjectAsset) => void;
  emptyText?: string;
  emptySubText?: string;
  onEmptyAction?: () => void;
  emptyActionText?: string;
}

// 检查是否为认证角色
const isVerifiedCharacter = (asset: IProjectAsset): boolean => {
  if (asset.assetType !== AssetType.CHARACTER) return false;
  if (!asset.assetId) return false;
  if (!asset.conceptImageUrl) return false;

  // 检查 conceptImageUrl 中是否包含 assetId 参数
  try {
    const url = new URL(asset.conceptImageUrl, window.location.origin);
    const assetIdParam = url.searchParams.get('assetId');
    return !!assetIdParam;
  } catch {
    return false;
  }
};

export default function AssetGrid({
  assets,
  loading,
  onEdit,
  onDelete,
  onGenerate,
  onUpload,
  emptyText,
  emptySubText,
  onEmptyAction,
  emptyActionText
}: AssetGridProps) {
  const { t, i18n } = useTranslation(['projectSettings', 'common']);
  const characterRoleLabels = getLabelsByLocale(CharacterRoleLabels, i18n.language as Locale);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const getAssetPreview = (asset: IProjectAsset) => {
    return asset.conceptImageUrl || asset.frontViewUrl || asset.sideViewUrl || asset.backViewUrl || asset.detailImageUrl || null;
  };

  if (loading) {
    return (
      <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="bg-bg-surface rounded-2xl overflow-hidden border border-border-subtle animate-pulse">
            <div className="aspect-square bg-gray-700" />
            <div className="p-3 space-y-2">
              <div className="h-4 bg-gray-700 rounded w-2/3" />
              <div className="h-3 bg-gray-700 rounded w-full" />
              <div className="flex gap-2">
                <div className="flex-1 h-8 bg-gray-700 rounded-lg" />
                <div className="flex-1 h-8 bg-gray-700 rounded-lg" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (assets.length === 0) {
    return (
      <div className="text-center py-12">
        <PhotoIcon className="h-12 w-12 text-gray-600 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-400 mb-2">{emptyText || t('asset.empty')}</h3>
        {emptySubText && <p className="text-gray-500 mb-6">{emptySubText}</p>}
        {onEmptyAction && (
          <button
            onClick={onEmptyAction}
            className="bg-gradient-to-br from-primary to-primary-light hover:shadow-[0_0_20px_rgba(255,92,0,0.3)] text-white px-6 py-3 rounded-lg transition-all"
          >
            {emptyActionText || t('asset.createAsset')}
          </button>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4">
        {assets.map((asset) => {
          const previewUrl = getAssetPreview(asset);
          const generationStatus = asset.generationStatus || AssetGenerationStatus.IDLE;
          const isGenerating = generationStatus === AssetGenerationStatus.PENDING ||
            generationStatus === AssetGenerationStatus.GENERATING;
          const isVerified = isVerifiedCharacter(asset);

          return (
            <div
              key={asset.id}
              className={`bg-bg-surface rounded-2xl overflow-hidden border border-border-subtle hover:border-primary hover:-translate-y-1 hover:shadow-[0_0_20px_rgba(255,92,0,0.15)] transition-all duration-300 ${previewUrl ? 'cursor-pointer' : ''}`}
              onClick={() => previewUrl && setPreviewImage(previewUrl)}
            >
              <div className="aspect-square bg-bg-elevated relative overflow-hidden">
                {previewUrl ? (
                  <LazyImage
                    src={previewUrl}
                    alt={asset.name}
                    className="w-full h-full object-cover"
                    placeholderClassName="w-full h-full"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <PhotoIcon className="h-10 w-10 text-gray-500" />
                  </div>
                )}
                {/* 认证角标 - 金色徽章 */}
                {isVerified && (
                  <div
                    className="absolute top-2 right-2 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full p-1.5 shadow-lg ring-2 ring-yellow-300/50"
                    title={t('asset.verified')}
                  >
                    <CheckBadgeIcon className="h-4 w-4 text-white drop-shadow-md" />
                  </div>
                )}
                {/* 生成状态指示器 */}
                {isGenerating && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </div>

              <div className="p-3" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-medium text-gray-50 truncate text-sm flex-1">{asset.name}</h3>
                  {asset.assetType === AssetType.CHARACTER && asset.role && (
                    <span
                      className="px-1.5 py-0.5 text-[10px] rounded flex-shrink-0"
                      style={{
                        backgroundColor: `${CharacterRoleColors[asset.role]}20`,
                        color: CharacterRoleColors[asset.role]
                      }}
                    >
                      {characterRoleLabels[asset.role]}
                    </span>
                  )}
                </div>


                <div className="flex gap-1.5">
                  {/* 生成按钮 - 从文本生成 */}
                  {onGenerate && !isGenerating && (
                    <button
                      onClick={() => onGenerate(asset)}
                      className="flex-1 bg-primary/20 hover:bg-primary/30 text-primary p-1.5 rounded-lg text-xs transition-all flex items-center justify-center"
                      title={t('subject.aiTextGenerate')}
                    >
                      <SparklesIcon className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {/* 上传按钮 - 从图片生成 */}
                  {onUpload && !isGenerating && (
                    <button
                      onClick={() => onUpload(asset)}
                      className="flex-1 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 p-1.5 rounded-lg text-xs transition-all flex items-center justify-center"
                      title={t('subject.uploadRefGenerate')}
                    >
                      <ArrowUpTrayIcon className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {/* 编辑按钮 */}
                  <button
                    onClick={() => onEdit(asset)}
                    className="flex-1 bg-bg-elevated hover:bg-border-default text-gray-300 hover:text-white p-1.5 rounded-lg text-xs transition-all flex items-center justify-center"
                    title={t('common:edit')}
                  >
                    <PencilIcon className="h-3.5 w-3.5" />
                  </button>
                  {/* 删除按钮 */}
                  <button
                    onClick={() => onDelete(asset)}
                    className="flex-1 bg-bg-elevated hover:bg-red-500/20 text-gray-300 hover:text-red-400 p-1.5 rounded-lg text-xs transition-all flex items-center justify-center"
                    title={t('common:delete')}
                  >
                    <TrashIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <MediaPreviewModal
        isOpen={!!previewImage}
        onClose={() => setPreviewImage(null)}
        type="image"
        src={previewImage || ''}
        alt={t('asset.previewAlt')}
      />
    </>
  );
}
